const IDENTITY_2D = [1, 0, 0, 1, 0, 0];
const IDENTITY_3D = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/** True for a computed `transform` that is set but moves nothing (e.g. `translateZ(0)`). */
export function isIdentityTransform(transform: string): boolean {
	const match = /^matrix(3d)?\((.+)\)$/.exec(transform.trim());
	if (!match) return false;
	const values = match[2].split(',').map(Number);
	const identity = match[1] ? IDENTITY_3D : IDENTITY_2D;
	return (
		values.length === identity.length &&
		values.every((value, i) => value === identity[i])
	);
}

/**
 * Temporarily sets `transform: none` on every ancestor of `el` whose transform
 * is an identity, and returns a function that restores the original inline
 * values. Any transform (even `translateZ(0)`) makes an element the containing
 * block for `position: fixed` descendants; lifting identity ones puts `fixed`
 * back on the viewport without moving anything on screen.
 */
export function liftAncestorIdentityTransforms(el: HTMLElement): () => void {
	const lifted: { el: HTMLElement; value: string; priority: string }[] = [];
	let ancestor = el.parentElement;
	while (ancestor && ancestor !== document.body) {
		if (isIdentityTransform(getComputedStyle(ancestor).transform)) {
			lifted.push({
				el: ancestor,
				value: ancestor.style.getPropertyValue('transform'),
				priority: ancestor.style.getPropertyPriority('transform'),
			});
			ancestor.style.setProperty('transform', 'none', 'important');
		}
		ancestor = ancestor.parentElement;
	}
	return () => {
		for (const { el: liftedEl, value, priority } of lifted) {
			if (value) liftedEl.style.setProperty('transform', value, priority);
			else liftedEl.style.removeProperty('transform');
		}
	};
}
